import { queryClient } from 'index';
import i18n from 'providers/i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { LogyxError, getToastErrorMessage } from 'services/api/errors';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import {
  createTemplate,
  deleteTemplate,
  getTemplates,
  updateTemplate,
} from 'services/TemplateService';
import {
  ICreateTemplateData,
  ITemplate,
  ITemplateListItem,
  IUpdateTemplateData,
  TemplateVisibility,
} from 'types/Template.types';
import { RoutesConfig } from 'navigation/routes';
import { IOption } from 'components/Select/type';
import { getDataFromPages } from 'utils/getDataFromPages';
import { getNextPageParam } from 'utils/getNextPageParam';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import { useGetSupplierDataFromNavigationState } from 'pages/DealerPanel/DealerPanelModelsPage/hooks';
import {
  resetConfigureForm,
  setPredefinedPricing,
} from 'store/Model/actions/model';
import { setCurrentlyEditingShoppingCartLineData } from 'store/ShoppingCart/actions/shoppingCart';
import {
  setClearedExtraPriceQuestionSlugs,
  setIndicativeQuestionKeys,
  setSelectedConfigurationAnswers,
  setTestCompanyModelConfiguration,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  resetManuallyChangedQuestionNames,
  resetManuallyClearedQuestionNames,
  resetTouchedQuestionNames,
  setAreAllFieldsTouched,
  setIncludeBomItems,
} from 'store/Form/actions/form';

export const TEMPLATES_PER_PAGE = 10;

// `visibility` is part of the key, not just the request: the same model's list
// is scoped differently for the dealer panel (own templates) and the quotation
// picker ('all' — own plus global), so sharing a cache entry would let one show
// the other's rows.
export const useGetTemplatesInfinite = (
  modelId: number,
  searchBy: string,
  isEnabled = true,
  visibility?: TemplateVisibility
) =>
  useInfiniteQuery({
    queryKey: [
      ReactQueryKeys.GET_TEMPLATES_INFINITE,
      modelId,
      TEMPLATES_PER_PAGE,
      searchBy,
      visibility,
      i18n.resolvedLanguage,
    ],
    queryFn: ({ pageParam = 1 }) =>
      getTemplates(
        modelId,
        pageParam,
        TEMPLATES_PER_PAGE,
        searchBy,
        visibility
      ),
    getNextPageParam,
    enabled: isEnabled && !!modelId,
  });

export const useGetTemplateOptions = (
  modelId: number,
  searchBy: string,
  isEnabled = true
) => {
  const { data, fetchNextPage, hasNextPage, isFetching } =
    useGetTemplatesInfinite(modelId, searchBy, isEnabled, 'all');

  const templates: ITemplateListItem[] = getDataFromPages(
    data?.pages,
    'templates'
  );

  const templateOptions: IOption[] = templates
    .filter((template: ITemplateListItem) => template.is_valid !== false)
    .map((template: ITemplateListItem) => ({
      value: String(template.id),
      label: template.name,
      key: String(template.id),
    }));

  return { templateOptions, fetchNextPage, hasNextPage, isFetching };
};

export const useCreateTemplate = (
  onSuccess?: (template: ITemplate) => void
) => {
  const { t } = useTranslation();
  return useMutation(
    (createTemplateData: ICreateTemplateData) =>
      createTemplate(createTemplateData),
    {
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      onSuccess: (template: ITemplate) => {
        toast.success(t('Template created'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_TEMPLATES_INFINITE]);
        onSuccess?.(template);
      },
      mutationKey: ReactMutationKeys.CREATE_TEMPLATE,
    }
  );
};

export const useUpdateTemplate = (
  onSuccess?: (template: ITemplate) => void
) => {
  const { t } = useTranslation();
  return useMutation(
    (params: { templateId: number; dto: IUpdateTemplateData }) =>
      updateTemplate(params.templateId, params.dto),
    {
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      onSuccess: (template: ITemplate, params) => {
        toast.success(t('Template saved'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_TEMPLATES_INFINITE]);
        queryClient.invalidateQueries([
          ReactQueryKeys.GET_SINGLE_TEMPLATE,
          params.templateId,
        ]);
        onSuccess?.(template);
      },
      mutationKey: ReactMutationKeys.UPDATE_TEMPLATE,
    }
  );
};

export const useDeleteTemplate = () => {
  const { t } = useTranslation();
  return useMutation((templateId: number) => deleteTemplate(templateId), {
    onError: (error: LogyxError) => {
      toast.error(getToastErrorMessage(error));
    },
    onSuccess: (_data, templateId) => {
      toast.success(t('Deleted template'));
      queryClient.invalidateQueries([ReactQueryKeys.GET_TEMPLATES_INFINITE]);
      queryClient.removeQueries([
        ReactQueryKeys.GET_SINGLE_TEMPLATE,
        templateId,
      ]);
    },
    mutationKey: ReactMutationKeys.DELETE_TEMPLATE,
  });
};

// Entering the configurator in template mode. Mirrors handleConfigureLine on
// BuyerSingleQuotationPage: the configurator is shared state, so everything the
// previous session left behind has to be cleared before we hand it a model, or
// the template inherits a cart line, a test configuration or a stale pricing
// context that has nothing to do with it.
export const useNavigateToTemplateConfigurator = () => {
  const dispatch = useDispatch();
  const navigateWithQuery = useNavigateWithQuery();
  // TemplatesModal renders inside DealerPanelModelsPage, whose supplier context
  // lives in navigation state — carry it along so Back can restore the list.
  const supplierData = useGetSupplierDataFromNavigationState();

  return (modelId: number, templateId?: number) => {
    dispatch(resetConfigureForm());
    dispatch(setCurrentlyEditingShoppingCartLineData(null, null));
    dispatch(setTestCompanyModelConfiguration(null));
    dispatch(setPredefinedPricing(null));
    dispatch(setSelectedConfigurationAnswers([]));
    dispatch(setClearedExtraPriceQuestionSlugs([]));
    dispatch(setIndicativeQuestionKeys([]));
    dispatch(resetTouchedQuestionNames());
    dispatch(resetManuallyChangedQuestionNames());
    dispatch(resetManuallyClearedQuestionNames());
    dispatch(setAreAllFieldsTouched(false));
    dispatch(setIncludeBomItems(false));

    navigateWithQuery(
      RoutesConfig.Configure.fullPath,
      { state: supplierData },
      {
        modelId: modelId.toString(),
        isTemplate: 'true',
        ...(templateId && { templateId: templateId.toString() }),
      },
      // taxRate would put SubHeader into test mode, quotationId/lineId into
      // resolve mode, and a stale templateId would make a new template edit an
      // existing one.
      [
        'taxRate',
        'editVersionId',
        'quotationId',
        'lineId',
        'isEdit',
        ...(templateId ? [] : ['templateId']),
      ]
    );
  };
};
